import { PersistenceState, Image } from './../../shared/types/common'
import { Charger, ChargerModel, ChargerConnector } from './../../shared/types/charger'
import { sendJson, sendData } from './../../shared/utils/api'
import { LocationModel, Location, LocationInfo } from './../../shared/types/location'
import {
    asyncActionCreator,
    valueHolderActionCreator,
} from './../../shared/redux/actions/common'
import {
    FETCH_LOCATIONS,
    FETCH_LOCATION_FOR_DETAIL,
    SELECTED_LOCATION_ID,
    ADD_LOCATION,
    EDIT_LOCATION,
    DELETE_LOCATION,
    UPLOAD_LOCATION_PHOTOS,
    DELETE_LOCATION_PHOTO,
    ADD_CHARGER,
    EDIT_CHARGER,
    DELETE_CHARGER,
    RESET_CHARGER,
    UPDATE_CONNECTORS,
    UNLOCK_CONNECTOR,
    UPLOAD_CHARGER_PHOTOS,
    DELETE_CHARGER_PHOTO,
    CHANGE_CONNECTOR_AVAILABILITY,
    UPDATE_CONNECTOR_AVAILABILITY,
    UPLOAD_CHARGER_CSV,
    MAP_TOKEN,
    FETCH_CONNECTOR_PRICING_CONDITIONS,
    START_CHARGING,
    RESET_START_CHARGING_STATE,
    RESET_STOP_CHARGING_STATE,
    STOP_CHARGING,
    RESET_CHARGER_CONFIGURATION_STATE,
    UPDATE_LOCATION_WHITELIST,
    UPDATE_LOCATION_FLEXICHARGING,
} from '../const'
import { getJson, patchProperty } from '../../shared/utils/api'
import { LoadBalancer } from '../../shared/types/flexiCharging'

const startChargingErrorCodesMapping = [
    ['1', 'charging.communicationError'],
    ['2', 'charging.errorRejected'],
    ['3', 'charging.errorWrongState'],
    ['5', 'charging.errorProfileRejected'],
    ['6', 'charging.chargingSessionLimitReached'],
]

const stopChargingErrorCodesMapping = [
    ['1', 'charging.errorNotConnectedEndThroughCar'],
    ['2', 'charging.errorRejected'],
    ['3', 'charging.errorWrongState'],
]

export const addLocation = (location: LocationModel) => {
    return asyncActionCreator(ADD_LOCATION, () => sendJson(`locations`, 'POST', location))
}

export const editLocation = (location: Location) => {
    return asyncActionCreator(EDIT_LOCATION, () => {
        const patchOperations = [
            patchProperty('name', location.name),
            patchProperty('description', location.description),
            patchProperty('latitude', location.latitude),
            patchProperty('longitude', location.longitude),
            patchProperty('city', location.city),
            patchProperty('street', location.street),
            patchProperty('postalCode', location.postalCode),
            patchProperty('houseNum', location.houseNum),
            patchProperty('phoneNumber', location.phoneNumber),
            patchProperty('hidden', location.hidden),
            patchProperty('includeInHubject', location.includeInHubject),
            patchProperty('allowAnonymous', location.allowAnonymous),
            patchProperty('emailNotification', location.emailNotification),
            patchProperty('tenantSpecific', location.tenantSpecific),
            patchProperty('marketId', location.marketId),
            patchProperty('tenantId', location.tenantId),
        ]

        const payload = {
            operations: patchOperations.filter((op) => op.value !== undefined),
            tags: location.tags,
        }

        return sendJson(`locations/${location.id}`, 'PATCH', payload)
    })
}

export const deleteLocation = (location: LocationInfo) => {
    return asyncActionCreator(DELETE_LOCATION, () => sendJson(`locations/${location.id}`, 'DELETE'), {
        locationId: location.id,
    })
}

export const fetchLocations = (tenantId: string) => {
    const url = `locations/all?TenantIds=${tenantId}&showHidden=true&sort=name asc`
    return asyncActionCreator(FETCH_LOCATIONS, () => getJson(url))
}

export const fetchLocationForDetail = (locationId: string) => {
    return asyncActionCreator(FETCH_LOCATION_FOR_DETAIL, () => getJson(`locations/${locationId}`))
}

export const fetchPricingConditionsForConnector = (connectorId: string, userId?: string) => {
    let url = `chargingConnector/pricingConditions?connectorId=${connectorId}`
    if (userId) {
        url += `&userId=${userId}`
    }
    return asyncActionCreator(FETCH_CONNECTOR_PRICING_CONDITIONS, () => getJson(url))
}

export const setSelectedLocationId = (id: string | undefined) => {
    return valueHolderActionCreator(SELECTED_LOCATION_ID, id)
}

export const uploadPhotosForLocation = (location: LocationInfo, photos: File[]) => {
    const uploadFunction = () =>
        Promise.all(
            photos.map((photo) => {
                return sendData(`locations/image/${location.id}`, 'POST', photo)
            })
        )
    return asyncActionCreator(UPLOAD_LOCATION_PHOTOS, uploadFunction, {
        locationId: location.id,
    })
}

export const deleteLocationPhoto = (location: LocationInfo, photo: Image) => {
    return asyncActionCreator(DELETE_LOCATION_PHOTO, () => sendJson(`image/${photo.id}`, 'DELETE'), {
        locationId: location.id,
        photoId: photo.id,
    })
}

export const addCharger = (charger: ChargerModel) => {
    return asyncActionCreator(ADD_CHARGER, () => sendJson(`charger`, 'POST', charger), {}, [
        ['0', 'locations.chargerAlreadyExists'],
    ])
}

export const editCharger = (charger: Charger) => {
    return asyncActionCreator(EDIT_CHARGER, () => {
        const patchOperations = [
            patchProperty('locationId', charger.locationId),
            patchProperty('name', charger.name),
            patchProperty('manufacturer', charger.manufacturer),
            patchProperty('brand', charger.brand),
            patchProperty('textInstructionForNavigation', charger.textInstructionForNavigation),
            patchProperty('textInstructionHowToStartCharging', charger.textInstructionHowToStartCharging),
            patchProperty('serviceNote', charger.serviceNote),
            patchProperty('externalId', charger.externalId),
            patchProperty('manualFlexiCharging', charger.manualFlexiCharging),
            patchProperty('qVendTid', charger.qVendTid),
            patchProperty('villaProIntegration', charger.villaProIntegration)
        ]
        const payload = {
            operations: patchOperations.filter((op) => op.value !== undefined),
            tags: charger.tags,
        }

        return sendJson(`charger/${charger.id}`, 'PATCH', payload)
    })
}

export const editFlexiCharging = (
    locationId: string,
    chargers: Charger[],
    loadBalancers: LoadBalancer[],
    marketId: string,
    flexiCharging: boolean
) => {
    const payload = {
        chargers: chargers.map((c) => ({
            ...c,
            loadBalancerId: c.loadBalancerId ?? null,
            // Pridávame manualFlexiCharging do payloadu
            manualFlexiCharging: !!c.manualFlexiCharging, 
        })),
        loadBalancers: loadBalancers,
        marketId: marketId,
        flexiCharging: flexiCharging,
    }
    return asyncActionCreator(UPDATE_LOCATION_FLEXICHARGING, () =>
        sendJson(`locations/${locationId}/flexicharging/`, 'POST', payload)
    )
}

export const deleteCharger = (charger: Charger) => {
    return asyncActionCreator(DELETE_CHARGER, () => sendJson(`charger/${charger.id}`, 'DELETE'), {
        chargerId: charger.id,
    })
}

export const updateConnectorsForCharger = (charger: Charger, connectors: ChargerConnector[], locationId: string) => {
    const adjustBody = {
        connectorsToDelete: new Array<string>(),
        connectorsToUpdate: new Array<any>(),
        connectorsToAdd: new Array<any>(),
    }

    connectors.forEach((x) => {
        if (x.persistenceState === PersistenceState.Added) {
            adjustBody.connectorsToAdd.push(x)
        } else if (x.persistenceState === PersistenceState.Deleted) {
            adjustBody.connectorsToDelete.push(x.id)
        } else if (x.persistenceState === PersistenceState.Updated) {
            adjustBody.connectorsToUpdate.push({
                key: x.id,
                value: [
                    patchProperty('type', x.type),
                    patchProperty('socketType', x.socketType),
                    patchProperty('externalId', x.externalId),
                ],
            })
        }
    })

    return asyncActionCreator(
        UPDATE_CONNECTORS,
        () => sendJson(`chargingConnector/adjust?locationId=${locationId}`, 'POST', adjustBody),
        {
            chargerId: charger.id,
            deletedIds: adjustBody.connectorsToDelete,
        },
        [
            ['2', 'locations.connectorExternalIdsMustBeUnique'],
            ['3', 'locations.externalIdsAreMandatory'],
        ]
    )
}

export const uploadPhotosForCharger = (charger: Charger, photos: File[]) => {
    const uploadFunction = () =>
        Promise.all(
            photos.map((photo) => {
                return sendData(`charger/image/${charger.id}`, 'POST', photo)
            })
        )
    return asyncActionCreator(UPLOAD_CHARGER_PHOTOS, uploadFunction, {
        chargerId: charger.id,
    })
}

export const deleteChargerPhoto = (charger: Charger, photo: Image) => {
    return asyncActionCreator(DELETE_CHARGER_PHOTO, () => sendJson(`image/${photo.id}`, 'DELETE'), {
        chargerId: charger.id,
        photoId: photo.id,
    })
}

export const resetCharger = (charger: Charger, resetType: number) => {
    return asyncActionCreator(RESET_CHARGER, () => sendJson(`charger/reset/${charger.id}/${resetType}`, 'POST'))
}

export const changeConnectorAvailability = (connectorId: string, availType: number) => {
    return asyncActionCreator(CHANGE_CONNECTOR_AVAILABILITY, () =>
        sendJson(`chargingConnector/changeAvailability/${connectorId}/${availType}`, 'POST')
    )
}

export const updateConnectorAvailability = (chargerId: string, connectorId: string, availType: number) => {
    return valueHolderActionCreator(UPDATE_CONNECTOR_AVAILABILITY, { chargerId, connectorId, availType })
}

export const unlockConnector = (connectorId: string) => {
    return asyncActionCreator(UNLOCK_CONNECTOR, () => sendJson(`chargingConnector/unlock/${connectorId}`, 'POST'))
}
export const uploadChargerCsv = (chargerId: string, uploadType: number, fileToUpload: File) => {
    const uploadFunction = () => sendData(`charger/sendLocalListCsv/${chargerId}/${uploadType}`, 'POST', fileToUpload) // No need for Promise.all

    return asyncActionCreator(UPLOAD_CHARGER_CSV, uploadFunction, {
        chargerId: chargerId,
    })
}

export const setMapToken = (token: string) => {
    return valueHolderActionCreator(MAP_TOKEN, token)
}

export const startCharging = (userId: string, connectorId: string, pricingConditionId: string) => {
    const startChargingFunction = () =>
        sendJson(`chargingSession/start`, 'POST', {
            chargingConnectorId: connectorId,
            pricingConditionId,
            userId,
            forceStart: true,
        })

    return asyncActionCreator(START_CHARGING, startChargingFunction, {}, startChargingErrorCodesMapping)
}

export const stopCharging = (sessionId: string) => {
    const stopChargingFunction = () => sendJson(`chargingSession/end/${sessionId}`, 'PUT')

    return asyncActionCreator(STOP_CHARGING, stopChargingFunction, {}, stopChargingErrorCodesMapping)
}

export const resetStartChargingState = () => ({
    type: RESET_START_CHARGING_STATE,
})
export const resetStoptChargingState = () => ({
    type: RESET_STOP_CHARGING_STATE,
})
export const resetChargerConfigurationState = () => ({
    type: RESET_CHARGER_CONFIGURATION_STATE,
})

export const updateLocationWhitelist = (locationId: string, users: string[]) => {
    return asyncActionCreator(UPDATE_LOCATION_WHITELIST, () =>
        sendJson(`locations/${locationId}/whitelist/`, 'POST', users)
    )
}
